import Header from "../components/header/Header";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import dayjs from "dayjs";
import {
    Button,
    Card,
    Col,
    DatePicker,
    Divider,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from "antd";
import { CloseOutlined, MinusOutlined, PlusOutlined, PrinterOutlined, MergeCellsOutlined, PauseCircleOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { apiRequest } from "../utils/apiClient";
import checkPosType from "../utils/posType";
import { generateInvoiceNumber } from "../utils/generateInvoiceNumber";
import getPakistanDateTime from "../utils/dateTimeUtils";
import {
    sendInvoiceToFBR,
    sendPRAInvoiceToFBR,
} from "../utils/fbrApi";
import {
    DINING_DB_NAME,
    DINING_DB_VERSION,
    DINING_STORE_TABLES,
    DINING_STORE_ORDERS,
    DINING_STORE_FREQUENT,
    DINING_STORE_SETTINGS,
    DINING_STORE_KITCHEN_RECEIPTS,
    DINING_STORE_HELD_ORDERS,
    DINING_PER_HEAD_SETTING_KEY,
    DINING_DEFAULT_PER_HEAD_BASE,
    DINING_KITCHEN_TOKEN_SETTING_KEY,
    DINING_DEFAULT_KITCHEN_TOKEN,
    UOM_NUMBERS_PIECES_UNITS,
    INVOICE_TYPE_POS,
    PAYMENT_MODE_CASH,
    PAYMENT_MODE_CREDIT,
    PAYMENT_MODE_DEBIT_CARD,
    PAYMENT_MODE_CASH_CODE,
    PAYMENT_MODE_CREDIT_CODE,
    PAYMENT_MODE_DEBIT_CARD_CODE,
    POS_FEE,
    PRA_PER_HEAD_ITEM_CODE,
    PRA_PER_HEAD_PCT_CODE,
    PHARMACY_TAX_EXEMPT_KEYWORDS,
    PHARMACY_TAX_18_PERCENT_KEYWORDS,
} from "../utils/constants";

const { Title, Text } = Typography;

function openDiningDB() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DINING_DB_NAME, DINING_DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(DINING_STORE_TABLES)) {
                db.createObjectStore(DINING_STORE_TABLES, { keyPath: "id" });
            }
            if (!db.objectStoreNames.contains(DINING_STORE_ORDERS)) {
                db.createObjectStore(DINING_STORE_ORDERS, { keyPath: "tableId" });
            }
            if (!db.objectStoreNames.contains(DINING_STORE_FREQUENT)) {
                db.createObjectStore(DINING_STORE_FREQUENT, { keyPath: "_id" });
            }
            if (!db.objectStoreNames.contains(DINING_STORE_SETTINGS)) {
                db.createObjectStore(DINING_STORE_SETTINGS, { keyPath: "key" });
            }
            if (!db.objectStoreNames.contains(DINING_STORE_KITCHEN_RECEIPTS)) {
                db.createObjectStore(DINING_STORE_KITCHEN_RECEIPTS, { keyPath: "id" });
            }
            if (!db.objectStoreNames.contains(DINING_STORE_HELD_ORDERS)) {
                db.createObjectStore(DINING_STORE_HELD_ORDERS, { keyPath: "id" });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

function txDone(tx) {
    return new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
    });
}

async function idbGetAll(db, storeName) {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readonly");
        const store = tx.objectStore(storeName);
        const req = store.getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
    });
}

async function idbGet(db, storeName, key) {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readonly");
        const store = tx.objectStore(storeName);
        const req = store.get(key);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error);
    });
}

async function idbPut(db, storeName, value) {
    const tx = db.transaction(storeName, "readwrite");
    tx.objectStore(storeName).put(value);
    await txDone(tx);
}

async function idbDelete(db, storeName, key) {
    const tx = db.transaction(storeName, "readwrite");
    tx.objectStore(storeName).delete(key);
    await txDone(tx);
}

async function saveKitchenReceiptToDb(db, slipData) {
    if (!db || !slipData?.token) return;
    if (!db.objectStoreNames.contains(DINING_STORE_KITCHEN_RECEIPTS)) return;
    const id = `receipt_${slipData.token}_${Date.now()}`;
    const record = {
        id,
        token: slipData.token,
        tableName: slipData.tableName || "",
        waiterName: slipData.waiterName || "",
        orderNote: slipData.orderNote || "",
        items: Array.isArray(slipData.items)
            ? slipData.items.map((it) => ({ title: it.title || "", quantity: Number(it.quantity) || 0, note: it.note || "" }))
            : [],
        datetime: slipData.datetime || "",
        savedAt: Date.now(),
    };
    await idbPut(db, DINING_STORE_KITCHEN_RECEIPTS, record);
}

async function loadKitchenReceiptsFromDb(db) {
    if (!db) return [];
    if (!db.objectStoreNames.contains(DINING_STORE_KITCHEN_RECEIPTS)) return [];
    const list = await idbGetAll(db, DINING_STORE_KITCHEN_RECEIPTS);
    return (list || []).sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
}

async function clearKitchenReceiptsFromDb(db) {
    if (!db || !db.objectStoreNames.contains(DINING_STORE_KITCHEN_RECEIPTS)) return;
    const tx = db.transaction(DINING_STORE_KITCHEN_RECEIPTS, "readwrite");
    tx.objectStore(DINING_STORE_KITCHEN_RECEIPTS).clear();
    await txDone(tx);
}

async function loadHeldOrdersFromDb(db) {
    if (!db || !db.objectStoreNames.contains(DINING_STORE_HELD_ORDERS)) return [];
    const list = await idbGetAll(db, DINING_STORE_HELD_ORDERS);
    return (list || []).sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
}

async function saveHeldOrderToDb(db, held) {
    if (!db || !held?.id || !db.objectStoreNames.contains(DINING_STORE_HELD_ORDERS)) return;
    await idbPut(db, DINING_STORE_HELD_ORDERS, { ...held, savedAt: Date.now() });
}

async function deleteHeldOrderFromDb(db, id) {
    if (!db || !id || !db.objectStoreNames.contains(DINING_STORE_HELD_ORDERS)) return;
    await idbDelete(db, DINING_STORE_HELD_ORDERS, id);
}

// -----------------------------
// Tax helpers (mirrors cart logic)
// -----------------------------
function calculatePharmacyTax(medName) {
    const name = String(medName || "").toLowerCase();
    if (PHARMACY_TAX_EXEMPT_KEYWORDS.some((f) => name.includes(f))) {
        return 0;
    }
    if (PHARMACY_TAX_18_PERCENT_KEYWORDS.some((f) => name.includes(f))) {
        return 18;
    }
    return 0;
}

const normalizeTaxRate = (rate) => {
    if (rate === null || rate === undefined) return 0;
    if (String(rate).toLowerCase() === "exempt") return 0;
    const numericRate = parseFloat(String(rate).replace("%", ""));
    return Number.isNaN(numericRate) ? 0 : numericRate;
};

function calculateTax(price, taxRate) {
    return Number(((Number(price || 0) * Number(taxRate || 0)) / 100).toFixed(2));
}

function formatPKR(amount) {
    const n = Number(amount || 0);
    return `Rs ${n.toFixed(2)}`;
}

// 80mm thermal slip: Arial/Helvetica for kitchen invoice
const THERMAL_FONT = "Arial, Helvetica, sans-serif";
const thermalSlipStyle = {
    width: "80mm",
    maxWidth: "80mm",
    minWidth: "80mm",
    margin: "0 auto",
    fontFamily: THERMAL_FONT,
    fontSize: "11px",
    // Make all kitchen text bold so it prints darker/crisper on thermal printers
    fontWeight: 600,
    lineHeight: 1.3,
    padding: "4mm",
    background: "#fff",
    color: "#000",
    boxSizing: "border-box",
};
const thermalSlipHeaderStyle = {
    textAlign: "center",
    marginBottom: "3mm",
    borderBottom: "1px dashed #000",
    paddingBottom: "2mm",
};
const thermalSlipTokenStyle = {
    fontSize: "14px",
    fontWeight: 700,
    letterSpacing: "1px",
};
const thermalSlipMetaStyle = {
    marginTop: "2mm",
    fontSize: "10px",
};
const thermalSlipTableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: THERMAL_FONT,
    fontSize: "11px",
};
const thermalSlipThStyle = {
    textAlign: "left",
    padding: "1mm 0",
    borderBottom: "1px solid #000",
    fontWeight: 700,
};
const thermalSlipTdStyle = {
    padding: "1mm 0",
    borderBottom: "1px dotted #333",
    verticalAlign: "top",
    fontWeight: 600,
};

const DiningPage = () => {
    const { t } = useTranslation("pages");
    const { t: tCommon } = useTranslation("common");
    const dbRef = useRef(null);
    const navigate = useNavigate();
    const [perHeadBase, setPerHeadBase] = useState(DINING_DEFAULT_PER_HEAD_BASE);

    const [isReady, setIsReady] = useState(false);
    const [dbError, setDbError] = useState("");

    const [tables, setTables] = useState([]);
    const [ordersByTable, setOrdersByTable] = useState({});
    const [selectedTableId, setSelectedTableId] = useState(null);

    // Frequent selling items (persisted in IndexedDB)
    const [frequentItems, setFrequentItems] = useState([]);
    const [frequentModalOpen, setFrequentModalOpen] = useState(false);
    const [frequentSearch, setFrequentSearch] = useState("");
    const [frequentProducts, setFrequentProducts] = useState([]);
    const [frequentProductsLoading, setFrequentProductsLoading] = useState(false);

    const [products, setProducts] = useState([]);
    const [productSearch, setProductSearch] = useState("");
    const [productsLoading, setProductsLoading] = useState(false);

    const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
    const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
    const [discountPercent, setDiscountPercent] = useState(0);
    const [furtherTaxAmount, setFurtherTaxAmount] = useState(0);
    const [paymentMode, setPaymentMode] = useState(PAYMENT_MODE_CASH);
    const [invoiceForm] = Form.useForm();
    const user = useSelector((state) => state.user);

    // Kitchen order: token number and slip modal
    const [kitchenToken, setKitchenToken] = useState(DINING_DEFAULT_KITCHEN_TOKEN);
    const [kitchenModalOpen, setKitchenModalOpen] = useState(false);
    const [kitchenSlipData, setKitchenSlipData] = useState(null);
    const kitchenSlipRef = useRef(null);

    // Token history: stored receipts from IndexedDB
    const [tokenHistoryModalOpen, setTokenHistoryModalOpen] = useState(false);
    const [tokenHistoryList, setTokenHistoryList] = useState([]);
    const [tokenHistoryLoading, setTokenHistoryLoading] = useState(false);
    const [viewingReceipt, setViewingReceipt] = useState(null);
    const [reprintReceipt, setReprintReceipt] = useState(null);
    const reprintSlipRef = useRef(null);

    // Merge table
    const [mergeModalOpen, setMergeModalOpen] = useState(false);
    const [mergeTargetTableId, setMergeTargetTableId] = useState(null);

    // Held orders
    const [heldOrdersModalOpen, setHeldOrdersModalOpen] = useState(false);
    const [heldOrdersList, setHeldOrdersList] = useState([]);
    const [heldOrdersLoading, setHeldOrdersLoading] = useState(false);

    // Bill modal (show bill in modal, print from there – no popup)
    const [billModalOpen, setBillModalOpen] = useState(false);
    const billPrintRef = useRef(null);

    // Bulk add tables
    const [bulkTablesModalOpen, setBulkTablesModalOpen] = useState(false);
    const [bulkTablesCount, setBulkTablesCount] = useState(10);
    const [bulkTablesSubmitting, setBulkTablesSubmitting] = useState(false);

    // Bulk delete tables
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const [bulkDeleteSelectedKeys, setBulkDeleteSelectedKeys] = useState([]);
    const [bulkDeleteSubmitting, setBulkDeleteSubmitting] = useState(false);

    const invoiceType = user?.invoiceType ?? null;
    const businessName = user?.businessName ?? null;
    const isRestaurantHotelPos = checkPosType(businessName);


    const selectedTable = useMemo(
        () => tables.find((t) => t.id === selectedTableId) || null,
        [tables, selectedTableId]
    );

    const selectedOrder = useMemo(() => {
        if (!selectedTableId) return { tableId: null, items: [] };
        return ordersByTable[selectedTableId] || { tableId: selectedTableId, items: [] };
    }, [ordersByTable, selectedTableId]);

    const busyTableIds = useMemo(() => {
        const s = new Set();
        for (const [tableId, order] of Object.entries(ordersByTable)) {
            if (order?.items?.length) s.add(tableId);
        }
        return s;
    }, [ordersByTable]);

    const frequentItemIds = useMemo(() => {
        return new Set(frequentItems.map((i) => i._id));
    }, [frequentItems]);

    const currentPerHead = useMemo(
        () => (selectedOrder?.perHead ?? perHeadBase),
        [selectedOrder, perHeadBase]
    );

    const guestCount = useMemo(
        () => Math.max(1, Math.floor(Number(selectedOrder?.guestCount) || 1)),
        [selectedOrder?.guestCount]
    );

    const totals = useMemo(() => {
        const items = selectedOrder?.items || [];
        const subTotal = items.reduce(
            (sum, it) => sum + (Number(it.price || 0) * Number(it.quantity || 0)),
            0
        );
        const perHeadRate = selectedOrder?.perHead ?? perHeadBase;
        const perHeadTotal = perHeadRate * guestCount;

        // Calculate tax: if Debit Card is selected and it's a restaurant/hotel POS, apply 5% tax on subtotal
        let totalTax;
        if (
            invoiceType === INVOICE_TYPE_POS &&
            isRestaurantHotelPos &&
            paymentMode === PAYMENT_MODE_DEBIT_CARD
        ) {
            totalTax = (subTotal * 5) / 100;
        } else {
            totalTax = items.reduce((sum, it) => sum + Number(it.tax || 0), 0);
        }

        const posFee = POS_FEE;
        const safeDiscount = Math.min(Math.max(Number(discountPercent || 0), 0), 100);
        const discountAmount = (subTotal * safeDiscount) / 100;
        const furtherTax = Math.max(0, Number(furtherTaxAmount || 0));
        const totalAmount = Math.max(
            subTotal - discountAmount + totalTax + posFee + perHeadTotal + furtherTax,
            0
        );
        const finalAmount = Math.round(totalAmount * 100) / 100;
        return { subTotal, totalTax, posFee, discountAmount, furtherTax, finalAmount, perHeadTotal };
    }, [selectedOrder, discountPercent, furtherTaxAmount, perHeadBase, guestCount, paymentMode, invoiceType, isRestaurantHotelPos]);

    // Helper to read FBR toggle from localStorage (controlled by header/menu)
    const getEnableFBR = () => {
        if (invoiceType === INVOICE_TYPE_POS) {
            const saved = localStorage.getItem("fbrToggleState");
            return saved !== null ? saved === "true" : true;
        }
        // Always enabled for non-POS invoice types
        return true;
    };

    useEffect(() => {
        let cancelled = false;
        async function boot() {
            try {
                const db = await openDiningDB();
                if (cancelled) return;
                dbRef.current = db;
                const [savedTables, savedOrders, savedFrequent] = await Promise.all([
                    idbGetAll(db, DINING_STORE_TABLES),
                    idbGetAll(db, DINING_STORE_ORDERS),
                    idbGetAll(db, DINING_STORE_FREQUENT),
                ]);

                // Load persisted per-head base (per person rate) from settings
                let loadedPerHeadBase = DINING_DEFAULT_PER_HEAD_BASE;
                let loadedKitchenToken = DINING_DEFAULT_KITCHEN_TOKEN;
                try {
                    const setting = await idbGet(db, DINING_STORE_SETTINGS, DINING_PER_HEAD_SETTING_KEY);
                    const value = Number(setting?.value);
                    if (!Number.isNaN(value) && value > 0) {
                        loadedPerHeadBase = value;
                    }
                } catch {
                    // If settings store/key not available, fall back to default
                }
                try {
                    const tokenSetting = await idbGet(db, DINING_STORE_SETTINGS, DINING_KITCHEN_TOKEN_SETTING_KEY);
                    const tokenVal = Number(tokenSetting?.value);
                    if (!Number.isNaN(tokenVal) && tokenVal >= 1) {
                        loadedKitchenToken = Math.floor(tokenVal);
                    }
                } catch {
                    // fall back to default
                }

                if (cancelled) return;
                savedTables.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
                const map = {};
                for (const o of savedOrders) map[o.tableId] = o;
                setTables(savedTables);
                setOrdersByTable(map);
                setFrequentItems(Array.isArray(savedFrequent) ? savedFrequent : []);
                setPerHeadBase(loadedPerHeadBase);
                setKitchenToken(loadedKitchenToken);
                setIsReady(true);
                setDbError("");
                if (savedTables.length && !selectedTableId) {
                    setSelectedTableId(savedTables[0].id);
                }
            } catch (e) {
                setDbError(e?.message || t("diningDbInitFailed"));
                setIsReady(true);
            }
        }
        boot();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function persistOrder(nextOrder) {
        const db = dbRef.current;
        if (!db) return;
        if (!nextOrder?.items?.length) {
            await idbDelete(db, DINING_STORE_ORDERS, nextOrder.tableId);
            return;
        }
        await idbPut(db, DINING_STORE_ORDERS, { ...nextOrder, updatedAt: Date.now() });
    }

    async function savePerHeadBaseToSettings(nextBase) {
        const db = dbRef.current;
        if (!db) return;
        const clean = Number(nextBase) || DINING_DEFAULT_PER_HEAD_BASE;
        await idbPut(db, DINING_STORE_SETTINGS, {
            key: DINING_PER_HEAD_SETTING_KEY,
            value: clean,
            updatedAt: Date.now(),
        });
        // Immediately reflect new default in state for this session
        setPerHeadBase(clean);
    }

    async function saveKitchenTokenToSettings(token) {
        const db = dbRef.current;
        if (!db) return;
        const val = Math.max(1, Math.floor(Number(token) || DINING_DEFAULT_KITCHEN_TOKEN));
        await idbPut(db, DINING_STORE_SETTINGS, {
            key: DINING_KITCHEN_TOKEN_SETTING_KEY,
            value: val,
            updatedAt: Date.now(),
        });
        setKitchenToken(val);
    }

    async function resetKitchenToken() {
        await saveKitchenTokenToSettings(DINING_DEFAULT_KITCHEN_TOKEN);
        const db = dbRef.current;
        if (db) await clearKitchenReceiptsFromDb(db);
        setTokenHistoryList([]);
        setViewingReceipt(null);
        message.success(t("kitchenTokenReset"));
    }

    function openKitchenModal() {
        if (!ensureSelectedTable()) return message.warning(t("selectTableFirst"));
        if (!selectedOrder?.items?.length) return message.warning(t("noItemsInThisTable"));
        setKitchenSlipData({
            token: kitchenToken,
            tableName: selectedTable?.name || t("bill"),
            waiterName: selectedOrder.waiterName ?? "",
            orderNote: selectedOrder.orderNote || "",
            items: selectedOrder.items.map((it) => ({
                title: it.title || t("product"),
                quantity: Number(it.quantity) || 0,
                note: it.note || "",
            })),
            datetime: dayjs().format("DD-MMM-YYYY hh:mm A"),
        });
        setKitchenModalOpen(true);
    }

    const incrementTokenAfterPrint = useCallback(() => {
        setKitchenToken((prev) => {
            const next = prev + 1;
            queueMicrotask(() => saveKitchenTokenToSettings(next));
            return next;
        });
    }, []);

    const handlePrintKitchenSlip = useReactToPrint({
        content: () => kitchenSlipRef.current,
        documentTitle: `Kitchen-Order-${kitchenSlipData?.token ?? ""}`,
        onAfterPrint: () => {
            if (kitchenSlipData && dbRef.current) {
                saveKitchenReceiptToDb(dbRef.current, kitchenSlipData).catch(() => {});
            }
            incrementTokenAfterPrint();
            message.success(t("kitchenOrderPrinted"));
        },
    });

    const onPrintClick = () => {
        // Brief delay so modal content is in the DOM before the print dialog opens
        setTimeout(() => handlePrintKitchenSlip(), 100);
    };

    const openTokenHistoryModal = useCallback(() => {
        setTokenHistoryModalOpen(true);
        setViewingReceipt(null);
    }, []);

    useEffect(() => {
        if (!tokenHistoryModalOpen || !dbRef.current) return;
        let cancelled = false;
        setTokenHistoryLoading(true);
        loadKitchenReceiptsFromDb(dbRef.current)
            .then((list) => {
                if (!cancelled) setTokenHistoryList(list);
            })
            .finally(() => {
                if (!cancelled) setTokenHistoryLoading(false);
            });
        return () => { cancelled = true; };
    }, [tokenHistoryModalOpen]);

    useEffect(() => {
        if (!heldOrdersModalOpen || !dbRef.current) return;
        let cancelled = false;
        setHeldOrdersLoading(true);
        loadHeldOrdersFromDb(dbRef.current)
            .then((list) => {
                if (!cancelled) setHeldOrdersList(list);
            })
            .finally(() => {
                if (!cancelled) setHeldOrdersLoading(false);
            });
        return () => { cancelled = true; };
    }, [heldOrdersModalOpen]);

    async function createTable() {
        const db = dbRef.current;
        if (!db) return;
        const usedNumbers = new Set();
        for (const t of tables) {
            const match = String(t.name || "").match(/Table\s+(\d+)/i);
            if (match?.[1]) usedNumbers.add(Number(match[1]));
        }
        let nextNumber = 1;
        while (usedNumbers.has(nextNumber)) nextNumber += 1;
        const id = `t_${Date.now()}_${Math.random().toString(16).slice(2)}`;
        const table = { id, name: `Table ${nextNumber}`, createdAt: Date.now() };
        await idbPut(db, DINING_STORE_TABLES, table);
        setTables((prev) => [...prev, table]);
        setSelectedTableId(id);
    }

    async function createTablesBulk(count) {
        const db = dbRef.current;
        if (!db) return;
        const num = Math.min(100, Math.max(2, Math.floor(Number(count) || 2)));
        const usedNumbers = new Set();
        for (const t of tables) {
            const match = String(t.name || "").match(/Table\s+(\d+)/i);
            if (match?.[1]) usedNumbers.add(Number(match[1]));
        }
        let nextNumber = 1;
        while (usedNumbers.has(nextNumber)) nextNumber += 1;
        const numbers = [];
        for (let i = 0; i < num; i++) {
            while (usedNumbers.has(nextNumber)) nextNumber += 1;
            numbers.push(nextNumber);
            usedNumbers.add(nextNumber);
        }
        const base = Date.now();
        const newTables = numbers.map((n, i) => ({
            id: `t_${base}_${i}_${Math.random().toString(16).slice(2)}`,
            name: `Table ${n}`,
            createdAt: base + i,
        }));
        await Promise.all(newTables.map((table) => idbPut(db, DINING_STORE_TABLES, table)));
        setTables((prev) => [...prev, ...newTables].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0)));
        setSelectedTableId(newTables[0].id);
        setBulkTablesModalOpen(false);
        message.success(t("tablesCreatedCount", { count: num }));
    }

    async function cancelTable(tableId) {
        const db = dbRef.current;
        if (!db) return;
        try {
            await Promise.all([
                idbDelete(db, DINING_STORE_TABLES, tableId),
                idbDelete(db, DINING_STORE_ORDERS, tableId),
            ]);

            setOrdersByTable((prev) => {
                const next = { ...prev };
                delete next[tableId];
                return next;
            });

            setTables((prev) => {
                const remaining = prev.filter((t) => t.id !== tableId);
                if (selectedTableId === tableId) {
                    setSelectedTableId(remaining[0]?.id || null);
                }
                return remaining;
            });

            message.info(t("tableCancelled"));
        } catch (e) {
            message.error(e?.message || t("failedToCancelTable"));
        }
    }

    async function deleteTablesBulk(tableIds) {
        const db = dbRef.current;
        if (!db || !Array.isArray(tableIds) || tableIds.length === 0) return;
        const idSet = new Set(tableIds);
        try {
            await Promise.all(
                tableIds.flatMap((id) => [
                    idbDelete(db, DINING_STORE_TABLES, id),
                    idbDelete(db, DINING_STORE_ORDERS, id),
                ])
            );
            setOrdersByTable((prev) => {
                const next = { ...prev };
                idSet.forEach((id) => delete next[id]);
                return next;
            });
            setTables((prev) => {
                const remaining = prev.filter((t) => !idSet.has(t.id));
                if (selectedTableId && idSet.has(selectedTableId)) {
                    setSelectedTableId(remaining[0]?.id || null);
                }
                return remaining;
            });
            setBulkDeleteModalOpen(false);
            setBulkDeleteSelectedKeys([]);
            message.success(t("tablesDeletedCount", { count: tableIds.length }));
        } catch (e) {
            message.error(e?.message || t("failedToCancelTable"));
        }
    }

    function ensureSelectedTable() {
        if (!selectedTableId) return false;
        return tables.some((t) => t.id === selectedTableId);
    }

    function upsertOrderItem(tableId, p) {
        const hs = p?.hscode || p?.hsCode;
        const taxRate = normalizeTaxRate(p?.salesTax);
        const price = Number(p?.price || 0);
        const title = p?.title || t("product");
        const uom = p?.uom || UOM_NUMBERS_PIECES_UNITS;

        const perUnitTax = isRestaurantHotelPos
            ? calculateTax(price, taxRate)
            : calculatePharmacyTax(title);

        setOrdersByTable((prev) => {
            const current = prev[tableId] || { tableId, items: [], perHead: perHeadBase, guestCount: 1 };
            const items = Array.isArray(current.items) ? [...current.items] : [];
            const idx = items.findIndex((x) => x._id === p._id);
            if (idx >= 0) {
                const existing = items[idx];
                items[idx] = {
                    ...existing,
                    quantity: Number(existing.quantity || 0) + 1,
                    tax: Number((Number(existing.tax || 0) + perUnitTax).toFixed(2)),
                    totalPrice: Number(
                        (Number(existing.totalPrice || 0) + price + perUnitTax).toFixed(2)
                    ),
                };
            } else {
                items.push({
                    ...p,
                    _id: p._id,
                    title,
                    price,
                    hscode: hs,
                    hsCode: hs,
                    uom,
                    quantity: 1,
                    taxRate,
                    tax: perUnitTax,
                    totalPrice: Number((price + perUnitTax).toFixed(2)),
                    note: "",
                });
            }
            const nextOrder = {
                tableId,
                items,
                perHead: current.perHead ?? perHeadBase,
                guestCount: current.guestCount ?? 1,
                orderNote: current.orderNote ?? "",
                waiterName: current.waiterName ?? "",
                updatedAt: Date.now(),
            };
            queueMicrotask(() => persistOrder(nextOrder));
            return { ...prev, [tableId]: nextOrder };
        });
    }

    function updatePerHeadForSelectedTable(nextPerHead) {
        if (!ensureSelectedTable()) return;
        const tableId = selectedTableId;
        const clean = Math.max(1, Number(nextPerHead) || perHeadBase);
        setOrdersByTable((prev) => {
            const current = prev[tableId] || { tableId, items: [], guestCount: 1 };
            const nextOrder = {
                ...current,
                perHead: clean,
                guestCount: current.guestCount ?? 1,
                orderNote: current.orderNote ?? "",
                waiterName: current.waiterName ?? "",
                updatedAt: Date.now(),
            };
            queueMicrotask(() => persistOrder(nextOrder));
            return { ...prev, [tableId]: nextOrder };
        });
    }

    function updateOrderMeta(updates) {
        if (!ensureSelectedTable()) return;
        const tableId = selectedTableId;
        setOrdersByTable((prev) => {
            const current = prev[tableId] || { tableId, items: [], guestCount: 1 };
            const nextOrder = {
                ...current,
                ...updates,
                updatedAt: Date.now(),
            };
            queueMicrotask(() => persistOrder(nextOrder));
            return { ...prev, [tableId]: nextOrder };
        });
    }

    function changeQty(productId, nextQty) {
        if (!ensureSelectedTable()) return;
        const tableId = selectedTableId;
        // AntD InputNumber emits `null` while user clears/edits; don't treat that as 0.
        if (nextQty === null || nextQty === undefined || nextQty === "") return;
        const qty = Math.max(0, Math.floor(Number(nextQty)));
        setOrdersByTable((prev) => {
            const current = prev[tableId] || { tableId, items: [] };
            const items = Array.isArray(current.items) ? [...current.items] : [];
            const idx = items.findIndex((x) => x._id === productId);
            if (idx < 0) return prev;
            const item = items[idx];
            const perUnitTax = calculateTax(item.price, item.taxRate);
            if (qty === 0) items.splice(idx, 1);
            else {
                items[idx] = {
                    ...item,
                    quantity: qty,
                    tax: Number((perUnitTax * qty).toFixed(2)),
                    totalPrice: Number(((item.price + perUnitTax) * qty).toFixed(2)),
                };
            }
            const nextOrder = {
                tableId,
                items,
                perHead: current.perHead ?? perHeadBase,
                guestCount: current.guestCount ?? 1,
                orderNote: current.orderNote ?? "",
                waiterName: current.waiterName ?? "",
                updatedAt: Date.now(),
            };
            queueMicrotask(() => persistOrder(nextOrder));
            const next = { ...prev };
            if (!items.length) delete next[tableId];
            else next[tableId] = nextOrder;
            return next;
        });
    }

    function removeItem(productId) {
        changeQty(productId, 0);
    }

    async function fetchProducts(search, setList, setLoading) {
        try {
            setLoading(true);
            const params = new URLSearchParams({ limit: "100", page: "1" });
            const q = String(search || "").trim();
            if (q) params.append("search", q);
            const data = await apiRequest(`/api/products/get-all?${params.toString()}`);
            setList(Array.isArray(data?.products) ? data.products : []);
        } catch (e) {
            message.error(e?.message || t("failedToLoadProducts"));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const t = setTimeout(
            () => fetchProducts(productSearch, setProducts, setProductsLoading),
            250
        );
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productSearch]);

    useEffect(() => {
        if (!frequentModalOpen) return;
        const t = setTimeout(
            () => fetchProducts(frequentSearch, setFrequentProducts, setFrequentProductsLoading),
            250
        );
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [frequentSearch, frequentModalOpen]);

    async function addFrequentItem(p) {
        const db = dbRef.current;
        if (!db || !p?._id) return;

        const hs = p?.hscode || p?.hsCode;
        const entry = {
            ...p,
            hsCode: hs,
            hscode: hs,
        };

        try {
            await idbPut(db, DINING_STORE_FREQUENT, entry);
            setFrequentItems((prev) => {
                const idx = prev.findIndex((it) => it._id === entry._id);
                if (idx >= 0) {
                    const next = [...prev];
                    next[idx] = { ...next[idx], ...entry };
                    return next;
                }
                return [...prev, entry];
            });
            message.success(t("addedToFrequentItems"));
        } catch (e) {
            message.error(e?.message || t("failedToAddFrequentItem"));
        }
    }

    async function removeFrequentItem(id) {
        const db = dbRef.current;
        if (!db || !id) return;
        try {
            await idbDelete(db, DINING_STORE_FREQUENT, id);
            setFrequentItems((prev) => prev.filter((it) => it._id !== id));
            message.success(t("removedFromFrequentItems"));
        } catch (e) {
            message.error(e?.message || t("failedToRemoveFrequentItem"));
        }
    }

    function openInvoiceModal() {
        if (!ensureSelectedTable()) return message.warning(t("selectTableFirst"));
        if (!selectedOrder?.items?.length) return message.warning(t("noItemsInThisTable"));
        setPaymentMode(PAYMENT_MODE_CASH); // Reset payment mode to Cash
        setInvoiceModalOpen(true);
        invoiceForm.resetFields();
        invoiceForm.setFieldsValue({
            invoiceDate: dayjs(),
            paymentMode: PAYMENT_MODE_CASH,
        });
    }

    async function finalizeAndFreeTable() {
        const db = dbRef.current;
        if (!db || !selectedTableId) return;
        await idbDelete(db, DINING_STORE_ORDERS, selectedTableId);
        setOrdersByTable((prev) => {
            const next = { ...prev };
            delete next[selectedTableId];
            return next;
        });
    }

    async function clearCurrentTableOrder() {
        if (!selectedTableId) return;
        await finalizeAndFreeTable();
        message.success(t("currentTableCleared"));
    }

    function mergeCurrentTableInto(targetTableId) {
        if (!selectedTableId || !targetTableId || selectedTableId === targetTableId) return;
        const sourceOrder = ordersByTable[selectedTableId];
        const targetOrder = ordersByTable[targetTableId];
        if (!sourceOrder?.items?.length) return;
        const mergedItems = [...(targetOrder?.items || []), ...sourceOrder.items];
        const perHead = targetOrder?.perHead ?? sourceOrder?.perHead ?? perHeadBase;
        const guestCount = targetOrder?.guestCount ?? sourceOrder?.guestCount ?? 1;
        const orderNote = [targetOrder?.orderNote, sourceOrder?.orderNote].filter(Boolean).join("; ") || "";
        const waiterName = targetOrder?.waiterName || sourceOrder?.waiterName || "";
        const nextTargetOrder = {
            tableId: targetTableId,
            items: mergedItems,
            perHead,
            guestCount,
            orderNote,
            waiterName,
            updatedAt: Date.now(),
        };
        setOrdersByTable((prev) => {
            const next = { ...prev, [targetTableId]: nextTargetOrder };
            delete next[selectedTableId];
            return next;
        });
        persistOrder(nextTargetOrder);
        finalizeAndFreeTable();
        setMergeModalOpen(false);
        setMergeTargetTableId(null);
        setSelectedTableId(targetTableId);
        message.success(t("mergeTableSuccess"));
    }

    async function holdCurrentOrder() {
        if (!ensureSelectedTable() || !selectedOrder?.items?.length) return message.warning(t("noItemsInThisTable"));
        const db = dbRef.current;
        if (!db) return;
        const id = `held_${Date.now()}_${Math.random().toString(16).slice(2)}`;
        const held = {
            id,
            tableName: selectedTable?.name || t("bill"),
            items: selectedOrder.items,
            perHead: selectedOrder.perHead ?? perHeadBase,
            guestCount: selectedOrder.guestCount ?? 1,
            orderNote: selectedOrder.orderNote ?? "",
            waiterName: selectedOrder.waiterName ?? "",
            savedAt: Date.now(),
        };
        await saveHeldOrderToDb(db, held);
        await finalizeAndFreeTable();
        setHeldOrdersList((prev) => [held, ...prev]);
        setHeldOrdersModalOpen(true);
        message.success(t("orderHeld"));
    }

    async function resumeHeldOrderToTable(heldId, targetTableId) {
        const held = heldOrdersList.find((h) => h.id === heldId);
        if (!held?.items?.length || !targetTableId) return;
        const db = dbRef.current;
        const existing = ordersByTable[targetTableId];
        const mergedItems = [...(existing?.items || []), ...held.items];
        const nextOrder = {
            tableId: targetTableId,
            items: mergedItems,
            perHead: existing?.perHead ?? held.perHead ?? perHeadBase,
            guestCount: existing?.guestCount ?? held.guestCount ?? 1,
            orderNote: existing?.orderNote || held.orderNote || "",
            waiterName: existing?.waiterName || held.waiterName || "",
            updatedAt: Date.now(),
        };
        setOrdersByTable((prev) => ({ ...prev, [targetTableId]: nextOrder }));
        if (db) await persistOrder(nextOrder);
        await deleteHeldOrderFromDb(db, heldId);
        setHeldOrdersList((prev) => prev.filter((h) => h.id !== heldId));
        setSelectedTableId(targetTableId);
        setHeldOrdersModalOpen(false);
        message.success(t("orderResumed"));
    }

    async function removeHeldOrder(heldId) {
        const db = dbRef.current;
        if (!heldId) return;
        try {
            if (db) await deleteHeldOrderFromDb(db, heldId);
            setHeldOrdersList((prev) => prev.filter((h) => h.id !== heldId));
            message.success(t("heldOrderRemoved"));
        } catch (e) {
            message.error(e?.message || t("operationFailed"));
        }
    }

    function openBillModal() {
        if (!selectedOrder?.items?.length) {
            message.warning(t("noItemsInThisTable"));
            return;
        }
        setBillModalOpen(true);
    }

    const handlePrintBill = useReactToPrint({
        content: () => billPrintRef.current,
        documentTitle: selectedTable ? `Bill-${selectedTable.name}` : "Bill",
        onAfterPrint: () => message.success(t("billPrinted")),
    });

    const onBillPrintClick = () => {
        setTimeout(() => handlePrintBill(), 100);
    };

    const handleReprintKot = useReactToPrint({
        content: () => reprintSlipRef.current,
        documentTitle: reprintReceipt ? `Kitchen-Order-${reprintReceipt.token}` : "KOT-Reprint",
        onAfterPrint: () => setReprintReceipt(null),
    });

    useEffect(() => {
        if (!reprintReceipt) return;
        const t = setTimeout(() => {
            if (reprintSlipRef.current) handleReprintKot();
        }, 300);
        return () => clearTimeout(t);
    }, [reprintReceipt]); // eslint-disable-line react-hooks/exhaustive-deps

    async function submitFbrInvoice(values) {
        const cartItems = selectedOrder?.items || [];
        const userId = user?.id || "";
        const { date, time } = getPakistanDateTime(new Date().toISOString());
        const invNo = generateInvoiceNumber();
        const totalQuantity = cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const round2 = (num) => Number(Number(num).toFixed(2));

        // Calculate discount amount and distribute proportionally to items
        const totalDiscountAmount = totals.discountAmount;
        
        // Calculate each item's share of the discount proportionally
        const calculateItemDiscount = (itemSaleValue) => {
            if (totalDiscountAmount <= 0 || totals.subTotal <= 0) return 0;
            return round2((itemSaleValue / totals.subTotal) * totalDiscountAmount);
        };

        const effectivePaymentMode = values.paymentMode;

        // Check if we need to apply 5% tax for Debit Card
        const shouldApplyDebitCardTax = 
            invoiceType === INVOICE_TYPE_POS &&
            isRestaurantHotelPos &&
            effectivePaymentMode === PAYMENT_MODE_DEBIT_CARD;
        
        // Calculate total tax for Debit Card (5% of subtotal) if applicable
        const debitCardTotalTax = shouldApplyDebitCardTax
            ? round2((totals.subTotal * 5) / 100)
            : null;

        const fbrItems = cartItems.map((item) => {
            const quantity = Number(item.quantity) || 0;
            const unitPrice = Number(item.price) || 0;
            const saleValue = quantity * unitPrice;
            const itemDiscount = calculateItemDiscount(saleValue);
            const taxRate = Number(item.taxRate) || 0;
            
            // Calculate tax: if Debit Card, distribute 5% tax proportionally, otherwise use item tax
            let taxCharged;
            if (shouldApplyDebitCardTax && debitCardTotalTax !== null) {
                // Distribute 5% tax proportionally based on item's sale value
                const itemProportion = totals.subTotal > 0 
                    ? saleValue / totals.subTotal 
                    : 0;
                taxCharged = round2(debitCardTotalTax * itemProportion);
            } else {
                taxCharged = Number(item.tax) || 0;
            }
            
            return {
                ItemCode: item._id || "",
                ItemName: item.title || t("product"),
                Quantity: quantity,
                PCTCode: item.hsCode || item.hscode || "",
                TaxRate: shouldApplyDebitCardTax ? 5 : taxRate, // Use 5% rate for Debit Card
                SaleValue: round2(saleValue),
                TaxCharged: taxCharged,
                Discount: itemDiscount,
                FurtherTax: 0,
                TotalAmount: round2(saleValue + taxCharged - itemDiscount),
                InvoiceType: 1,
            };
        });

        // PRA: add per-head charge as a separate line item (audit-compliant) and ensure FurtherTax on each item
        let itemsForPRA = fbrItems;
        let totalSaleValue = fbrItems.reduce((sum, item) => sum + (item.SaleValue || 0), 0);
        let totalTaxCharged = fbrItems.reduce((sum, item) => sum + (item.TaxCharged || 0), 0);
        let totalDiscount = fbrItems.reduce((sum, item) => sum + (item.Discount || 0), 0);
        let totalAmountFromItems = fbrItems.reduce((sum, item) => sum + (item.TotalAmount || 0), 0);
        let totalQuantityForInvoice = totalQuantity;

        if (isRestaurantHotelPos && totals.perHeadTotal > 0) {
            const perHeadSaleValue = round2(Number(currentPerHead || 0) * guestCount);
            const perHeadTaxCharged = 0; // Per-head cover charge; no additional tax unless required
            const perHeadLine = {
                ItemCode: PRA_PER_HEAD_ITEM_CODE,
                ItemName: t("perHead") + " (" + guestCount + " " + t("guests") + ")",
                Quantity: guestCount,
                PCTCode: PRA_PER_HEAD_PCT_CODE,
                TaxRate: 0,
                SaleValue: perHeadSaleValue,
                TaxCharged: perHeadTaxCharged,
                Discount: 0,
                FurtherTax: 0,
                TotalAmount: round2(perHeadSaleValue + perHeadTaxCharged),
                InvoiceType: 1,
            };
            itemsForPRA = [...fbrItems, perHeadLine];
            totalSaleValue = totalSaleValue + perHeadSaleValue;
            totalTaxCharged = totalTaxCharged + perHeadTaxCharged;
            totalAmountFromItems = totalAmountFromItems + perHeadLine.TotalAmount;
            totalQuantityForInvoice = totalQuantity + guestCount;
        }

        const furtherTaxInvoiceLevel = Math.max(0, Number(totals.furtherTax || 0));
        const fbrTotalAmount = totalAmountFromItems + POS_FEE + furtherTaxInvoiceLevel;

        const invoice = {
            InvoiceNumber: invNo,
            USIN: "USIN0",
            DateTime: `${date} ${time}`,
            TotalBillAmount: round2(fbrTotalAmount),
            TotalQuantity: totalQuantityForInvoice,
            TotalSaleValue: round2(totalSaleValue),
            TotalTaxCharged: round2(totalTaxCharged),
            Discount: round2(totalDiscount),
            FurtherTax: round2(furtherTaxInvoiceLevel),
            PaymentMode: effectivePaymentMode === PAYMENT_MODE_CASH
                ? PAYMENT_MODE_CASH_CODE
                : effectivePaymentMode === PAYMENT_MODE_DEBIT_CARD
                    ? PAYMENT_MODE_DEBIT_CARD_CODE
                    : PAYMENT_MODE_CREDIT_CODE,
            InvoiceType: 1,
            Items: itemsForPRA,
        };

        const enableFBR = getEnableFBR();
        const shouldSendToFBR = invoiceType === INVOICE_TYPE_POS && enableFBR;

        let fbrInvoiceNumber = null;
        let backendFbrInvoiceNumber = null;

        if (shouldSendToFBR) {
            if (isRestaurantHotelPos) {
                fbrInvoiceNumber = await sendPRAInvoiceToFBR({ userId, invoice });
            } else {
                fbrInvoiceNumber = await sendInvoiceToFBR({ userId, invoice });
            }

            backendFbrInvoiceNumber =
                fbrInvoiceNumber?.fbrResponse?.invoiceNumber ||
                fbrInvoiceNumber?.fbrResponse?.InvoiceNumber ||
                "0";

            const fbrCode = fbrInvoiceNumber?.fbrResponse?.Code;
            const isFbrValid = fbrCode === "100";
            if (!isFbrValid) {
                const errorMsg = fbrInvoiceNumber?.fbrResponse?.Response || fbrInvoiceNumber?.fbrResponse?.error || t("fbrRejectedInvoice");
                throw new Error(errorMsg);
            }
        } else {
            // FBR is disabled for POS invoice - set to null
            backendFbrInvoiceNumber = null;
        }

        // Send total discounted amount (not percentage) to server
        const discountAmountForDb = totalDiscountAmount > 0 ? Number(totalDiscountAmount.toFixed(2)) : null;

        const effectiveBranchId = user?.branchId || user?.selectedBranchId;
        const invoicePayload = {
            ...values,
            invoiceType,
            subTotal: totals.subTotal.toFixed(2),
            tax: totals.totalTax.toFixed(2),
            totalAmount: totals.finalAmount.toFixed(2),
            cartItems,
            invoiceNumber: invNo,
            fbrInvoiceNumber: backendFbrInvoiceNumber !== null ? (backendFbrInvoiceNumber || "0") : null,
            userId: userId || "0",
            perHead: Number(currentPerHead || 0),
            guestCount: guestCount,
            discount: discountAmountForDb,
            paymentMode: values.paymentMode,
            furtherTax: Number(totals.furtherTax?.toFixed(2) ?? 0),
        };
        if (effectiveBranchId) invoicePayload.branchId = effectiveBranchId;
        await apiRequest("/api/invoices/add-invoice", {
            method: "POST",
            body: JSON.stringify(invoicePayload),
        });
    }

    const handlePlus = () => {
        updatePerHeadForSelectedTable(currentPerHead + perHeadBase);
        updateOrderMeta({ guestCount: Math.min(99, guestCount + 1) });
    };

    const handleMinus = () => {
        const next = currentPerHead > perHeadBase ? currentPerHead - perHeadBase : perHeadBase;
        updatePerHeadForSelectedTable(next);
        updateOrderMeta({ guestCount: Math.max(1, guestCount - 1) });
    };

    return (
        <>
            <Header />
            <div style={{ padding: 16, maxWidth: 1400, margin: "0 auto" }}>
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    {/* Current token banner – cashier always sees latest token */}
                    <Card size="small" style={{ background: "#f0f7ff", borderColor: "#91caff" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Text strong style={{ color: "#1677ff" }}>{t("currentToken")}</Text>
                                <Tag color="blue" style={{ margin: 0, fontSize: 18, padding: "4px 12px", lineHeight: 1.5 }}>
                                    #{kitchenToken}
                                </Tag>
                                <Popconfirm
                                    title={t("resetTokenConfirm")}
                                    okText={tCommon("yes")}
                                    cancelText={tCommon("no")}
                                    onConfirm={resetKitchenToken}
                                >
                                    <Button size="small">{t("resetToken")}</Button>
                                </Popconfirm>
                            </div>
                            <Space>
                                <Button type="default" onClick={() => setHeldOrdersModalOpen(true)}>
                                    <UnorderedListOutlined /> {t("heldOrders")}
                                </Button>
                                <Button type="default" onClick={openTokenHistoryModal}>
                                    {t("tokenHistory")}
                                </Button>
                            </Space>
                        </div>
                    </Card>

                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <div>
                            <Title level={4} style={{ margin: 0 }}>
                                {t("dineInTableManagement")}
                            </Title>
                        </div>
                        <Space wrap>
                            <Button type="primary" onClick={createTable} disabled={!isReady || Boolean(dbError)}>
                                {t("newTable")}
                            </Button>
                            <Button onClick={() => setBulkTablesModalOpen(true)} disabled={!isReady || Boolean(dbError)}>
                                {t("bulkAddTables")}
                            </Button>
                            <Button danger onClick={() => { setBulkDeleteSelectedKeys([]); setBulkDeleteModalOpen(true); }} disabled={!isReady || Boolean(dbError) || tables.length === 0}>
                                {t("bulkDeleteTables")}
                            </Button>
                        </Space>
                    </div>

                    {dbError ? (
                        <Card size="small" style={{ borderColor: "#ffccc7", background: "#fff2f0" }}>
                            <Text type="danger">{dbError}</Text>
                        </Card>
                    ) : null}

                    <Row gutter={[12, 12]}>
                        <Col xs={24} lg={7}>
                            <Card
                                title={
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span>{t("tables")}</span>
                                        <Text type="secondary">{tables.length}</Text>
                                    </div>
                                }
                                size="small"
                            >
                                {!isReady ? (
                                    <div style={{ textAlign: "center", padding: 24 }}>
                                        <Spin />
                                    </div>
                                ) : tables.length ? (
                                    <div style={{ maxHeight: 600, overflowY: "auto", paddingRight: 4 }}>
                                        <Space direction="vertical" style={{ width: "100%" }} size={8}>
                                            {tables.map((table) => {
                                                const isSelected = table.id === selectedTableId;
                                                const isBusy = busyTableIds.has(table.id);
                                                return (
                                                    <Card
                                                        key={table.id}
                                                        size="small"
                                                        hoverable
                                                        onClick={() => setSelectedTableId(table.id)}
                                                        style={{
                                                            cursor: "pointer",
                                                            borderColor: isSelected ? "#40a9ff" : undefined,
                                                            boxShadow: isSelected ? "0 0 0 2px rgba(24,144,255,0.2)" : undefined,
                                                        }}
                                                    >
                                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                                            <div style={{ fontWeight: 600 }}>{table.name}</div>
                                                            <Space size={6}>
                                                                <Tag color={isBusy ? "orange" : "green"}>
                                                                    {isBusy ? t("busy") : t("free")}
                                                                </Tag>
                                                                <Popconfirm
                                                                    title={t("cancelTable")}
                                                                    okText={tCommon("yes")}
                                                                    cancelText={tCommon("no")}
                                                                        onConfirm={(e) => {
                                                                        e?.stopPropagation?.();
                                                                        cancelTable(table.id);
                                                                    }}
                                                                    onCancel={(e) => e?.stopPropagation?.()}
                                                                >
                                                                    <Button
                                                                        size="small"
                                                                        type="text"
                                                                        danger
                                                                        icon={<CloseOutlined style={{ fontSize: 10 }} />}
                                                                        style={{ padding: 0, height: 18, width: 18 }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        aria-label={`Cancel ${table.name}`}
                                                                    />
                                                                </Popconfirm>
                                                            </Space>
                                                        </div>
                                                        <div style={{ marginTop: 6 }}>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                                {isBusy ? `${ordersByTable[table.id]?.items?.length || 0} item(s)` : t("noItems")}
                                                            </Text>
                                                        </div>
                                                    </Card>
                                                );
                                            })}
                                        </Space>
                                    </div>
                                ) : (
                                    <Text type="secondary">{t("noTablesYet")}</Text>
                                )}
                            </Card>
                        </Col>

                        <Col xs={24} lg={9}>
                            <Card
                                title={
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 12,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <span>{t("menuItems")}</span>
                                        <Space>
                                            <Button onClick={() => setFrequentModalOpen(true)}>
                                                {t("frequentSelling")}
                                            </Button>
                                            <Input
                                                placeholder={t("searchItemsByName")}
                                                value={productSearch}
                                                onChange={(e) => setProductSearch(e.target.value)}
                                                style={{ width: 260 }}
                                                allowClear
                                            />
                                        </Space>
                                    </div>
                                }
                                size="small"
                            >
                                <div style={{ marginBottom: 8 }}>
                                    <Text type="secondary">
                                        {selectedTable ? (
                                            <>
                                                {t("addingTo")} <b>{selectedTable.name}</b>
                                                <Tag color={busyTableIds.has(selectedTable.id) ? "orange" : "green"} style={{ marginInlineStart: 8 }}>
                                                    {busyTableIds.has(selectedTable.id) ? t("busy") : t("free")}
                                                </Tag>
                                            </>
                                        ) : (
                                            t("selectTableToEnable")
                                        )}
                                    </Text>
                                </div>

                                {frequentItems.length > 0 && (
                                    <div style={{ marginBottom: 12 }}>
                                        <Text strong>{t("frequentItemsLabel")}</Text>
                                        <div
                                            style={{
                                                marginTop: 6,
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 8,
                                            }}
                                        >
                                            {frequentItems.map((item) => (
                                                <div
                                                    key={item._id}
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 4,
                                                        border: "1px solid #d9d9d9",
                                                        borderRadius: 16,
                                                        padding: "2px 6px 2px 10px",
                                                        background: "#fafafa",
                                                    }}
                                                >
                                                    <Button
                                                        size="small"
                                                        type="link"
                                                        style={{ padding: 0 }}
                                                        disabled={!selectedTableId}
                                                        onClick={() =>
                                                            selectedTableId &&
                                                            ensureSelectedTable() &&
                                                            upsertOrderItem(selectedTableId, item)
                                                        }
                                                    >
                                                        {item.title || t("product")}
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        type="text"
                                                        icon={<CloseOutlined style={{ fontSize: 10 }} />}
                                                        style={{ padding: 0, width: 18, height: 18 }}
                                                        onClick={() => removeFrequentItem(item._id)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {productsLoading ? (
                                    <div style={{ textAlign: "center", padding: 24 }}>
                                        <Spin />
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            maxHeight: 380,
                                            overflow: "auto",
                                        }}
                                    >
                                        <Table
                                            size="small"
                                            rowKey={(r) => r._id}
                                            pagination={false}
                                            dataSource={products.filter((p) => p && p._id)}
                                            columns={[
                                                {
                                                    title: t("item"),
                                                    dataIndex: "title",
                                                    render: (_, r) => (
                                                        <div style={{ minWidth: 180 }}>
                                                            <div style={{ fontWeight: 600 }}>{r.title || tCommon("untitled")}</div>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                                {formatPKR(r.price)}
                                                            </Text>
                                                        </div>
                                                    ),
                                                },
                                                {
                                                    title: t("action"),
                                                    width: 110,
                                                    align: "right",
                                                    render: (_, r) => (
                                                        <Button
                                                            type="primary"
                                                            size="small"
                                                            disabled={!selectedTableId}
                                                            onClick={() =>
                                                                ensureSelectedTable() &&
                                                                upsertOrderItem(selectedTableId, r)
                                                            }
                                                        >
                                                            {t("addItem")}
                                                        </Button>
                                                    ),
                                                },
                                            ]}
                                        />
                                    </div>
                                )}
                            </Card>
                        </Col>

                        <Col xs={24} lg={8}>
                            <Card
                                title={t("bill")}
                                size="small"
                                extra={
                                    <Space>
                                        <Popconfirm
                                            title={t("clearTable")}
                                            okText={tCommon("yes")}
                                            cancelText={tCommon("no")}
                                            onConfirm={clearCurrentTableOrder}
                                            disabled={!selectedTableId || !(selectedOrder?.items?.length > 0)}
                                        >
                                            <Button
                                                danger
                                                disabled={!selectedTableId || !(selectedOrder?.items?.length > 0)}
                                            >
                                                {t("clear")}
                                            </Button>
                                        </Popconfirm>
                                        <InputNumber
                                            min={0}
                                            max={100}
                                            value={discountPercent}
                                            onChange={(v) => setDiscountPercent(v ?? 0)}
                                            style={{ width: 120 }}
                                            addonAfter="%"
                                        />
                                        <Button
                                            type="primary"
                                            onClick={openInvoiceModal}
                                            disabled={!selectedTableId || !(selectedOrder?.items?.length > 0)}
                                        >
                                            {t("createInvoice")}
                                        </Button>
                                    </Space>
                                }
                            >
                                {!selectedTableId ? (
                                    <Text type="secondary">{t("selectTableToViewBill")}</Text>
                                ) : selectedOrder?.items?.length ? (
                                    <>
                                        <Space direction="vertical" size={8} style={{ width: "100%", marginBottom: 12 }}>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                                                <span style={{ fontWeight: 500 }}>{t("numberOfGuests")}</span>
                                                <InputNumber
                                                    min={1}
                                                    max={99}
                                                    value={guestCount}
                                                    onChange={(v) => updateOrderMeta({ guestCount: Math.max(1, Math.floor(Number(v) || 1)) })}
                                                    style={{ width: 64 }}
                                                />
                                                <span style={{ marginLeft: 8, fontWeight: 500 }}>{t("waiter")}</span>
                                                <Input
                                                    placeholder={t("waiterPlaceholder")}
                                                    value={selectedOrder.waiterName || ""}
                                                    onChange={(e) => updateOrderMeta({ waiterName: e.target.value })}
                                                    style={{ width: 120 }}
                                                    allowClear
                                                />
                                            </div>
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 12 }}>{t("orderNote")}</Text>
                                                <Input
                                                    placeholder={t("orderNotePlaceholder")}
                                                    value={selectedOrder.orderNote || ""}
                                                    onChange={(e) => updateOrderMeta({ orderNote: e.target.value })}
                                                    style={{ marginTop: 4 }}
                                                    allowClear
                                                />
                                            </div>
                                        </Space>

                                        <div style={{ width: "100%", overflowX: "auto", overflowY: "visible" }}>
                                            <Table
                                                size="small"
                                                rowKey={(r) => r._id}
                                                pagination={false}
                                                dataSource={selectedOrder.items}
                                                scroll={{ x: 330, y: 240 }}
                                                style={{ minWidth: 330 }}
                                                className="pos-table-no-gap"
                                                columns={[
                                                    {
                                                        title: t("item"),
                                                        dataIndex: "title",
                                                        width: 130,
                                                        ellipsis: true,
                                                        render: (_, r) => (
                                                            <div style={{ overflow: "hidden", minWidth: 0 }}>
                                                                <div
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                    }}
                                                                >
                                                                    {r.title || t("product")}
                                                                </div>
                                                                <Text
                                                                    type="secondary"
                                                                    style={{
                                                                        fontSize: 12,
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        display: "block",
                                                                    }}
                                                                >
                                                                    {formatPKR(r.price)}
                                                                </Text>
                                                            </div>
                                                        ),
                                                    },
                                                    {
                                                        title: t("qty"),
                                                        dataIndex: "quantity",
                                                        width: 100,
                                                        align: "center",
                                                        render: (_, r) => (
                                                            <Space size={2} wrap={false}>
                                                                <Button size="small" style={{ width: 22, padding: 0 }} onClick={() => changeQty(r._id, r.quantity - 1)}>
                                                                    -
                                                                </Button>
                                                                <InputNumber
                                                                    size="small"
                                                                    min={0}
                                                                    value={r.quantity}
                                                                    controls={false}
                                                                    style={{ width: 46 }}
                                                                    onChange={(v) => changeQty(r._id, v)}
                                                                />
                                                                <Button size="small" style={{ width: 22, padding: 0 }} onClick={() => changeQty(r._id, r.quantity + 1)}>
                                                                    +
                                                                </Button>
                                                            </Space>
                                                        ),
                                                    },
                                                    {
                                                        title: "",
                                                        width: 32,
                                                        align: "center",
                                                        render: (_, r) => (
                                                            <Popconfirm
                                                                title={t("removeItem")}
                                                                okText={t("remove")}
                                                                cancelText={tCommon("cancel")}
                                                                onConfirm={() => removeItem(r._id)}
                                                            >
                                                                <Button
                                                                    type="text"
                                                                    danger
                                                                    size="small"
                                                                    icon={<CloseOutlined />}
                                                                    style={{ padding: 0, width: 16, height: 16, fontSize: 10 }}
                                                                />
                                                            </Popconfirm>
                                                        ),
                                                    },
                                                    {
                                                        title: t("total"),
                                                        dataIndex: "totalPrice",
                                                        width: 68,
                                                        align: "right",
                                                        render: (v) => <b style={{ whiteSpace: "nowrap" }}>{formatPKR(v)}</b>,
                                                    },
                                                ]}
                                            />
                                        </div>

                                        <Divider style={{ margin: "12px 0" }} />

                                        <div
                                            style={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                alignItems: "center",
                                                gap: 8,
                                                marginBottom: 12,
                                                padding: "8px 0",
                                                borderBottom: "1px solid #f0f0f0",
                                            }}
                                        >
                                            <Text strong style={{ marginRight: 4 }}>{t("kitchenTokenLabel")}</Text>
                                            <Tag color="blue" style={{ margin: 0, fontSize: 14 }}>
                                                #{kitchenToken}
                                            </Tag>
                                            <Button type="default" size="small" onClick={openKitchenModal}>
                                                {t("sendToKitchen")}
                                            </Button>
                                            <Button size="small" icon={<PrinterOutlined />} onClick={openBillModal}>
                                                {t("printBill")}
                                            </Button>
                                            <Button size="small" icon={<MergeCellsOutlined />} onClick={() => { setMergeTargetTableId(null); setMergeModalOpen(true); }}>
                                                {t("mergeTable")}
                                            </Button>
                                            <Button size="small" icon={<PauseCircleOutlined />} onClick={holdCurrentOrder}>
                                                {t("holdOrder")}
                                            </Button>
                                        </div>

                                        <Space direction="vertical" style={{ width: "100%" }} size={6}>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                <Text>{t("subtotal")}</Text>
                                                <Text>{formatPKR(totals.subTotal)}</Text>
                                            </div>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                <Text>{t("govtTaxes")}</Text>
                                                <Text type="danger">+{formatPKR(totals.totalTax)}</Text>
                                            </div>

                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    gap: 8,
                                                }}
                                            >
                                                <Text>{t("perHead")} ({guestCount} {t("guests")})</Text>

                                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                    <Button
                                                        icon={<MinusOutlined />}
                                                        onClick={handleMinus}
                                                        disabled={currentPerHead <= perHeadBase}
                                                    />

                                                    <InputNumber
                                                        min={1}
                                                        value={currentPerHead}
                                                        onChange={async (value) => {
                                                            const num = Number(value);
                                                            if (!value || Number.isNaN(num) || num <= 0) {
                                                                updatePerHeadForSelectedTable(perHeadBase);
                                                                return;
                                                            }
                                                            updatePerHeadForSelectedTable(num);
                                                            await savePerHeadBaseToSettings(num);
                                                        }}
                                                        style={{ width: 75, textAlign: "center" }}
                                                    />

                                                    <Button icon={<PlusOutlined />} onClick={handlePlus} />
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                <Text>{t("discount")}</Text>
                                                <Text>-{formatPKR(totals.discountAmount)}</Text>
                                            </div>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    gap: 8,
                                                }}
                                            >
                                                <Text>{t("furtherTax")}</Text>
                                                <InputNumber
                                                    min={0}
                                                    step={1}
                                                    value={furtherTaxAmount}
                                                    onChange={(v) => setFurtherTaxAmount(Number(v) ?? 0)}
                                                    style={{ width: 90 }}
                                                    size="small"
                                                    formatter={(val) => (val != null ? String(Number(val)) : "0")}
                                                />
                                            </div>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                <Text>{t("posFee")}</Text>
                                                <Text>{formatPKR(totals.posFee)}</Text>
                                            </div>
                                            <Divider style={{ margin: "8px 0" }} />
                                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                <Text strong>{t("total")}</Text>
                                                <Text strong style={{ fontSize: 16 }}>
                                                    {formatPKR(totals.finalAmount)}
                                                </Text>
                                            </div>
                                        </Space>

                                    </>


                                ) : (
                                    <Text type="secondary">{t("noItemsYetAddFromProducts")}</Text>
                                )}
                            </Card>
                        </Col>
                    </Row>
                </Space>
            </div>

            <Modal
                title={t("frequentSellingItems")}
                open={frequentModalOpen}
                onCancel={() => {
                    setFrequentModalOpen(false);
                    setFrequentSearch("");
                }}
                footer={null}
                destroyOnHidden
            >
                <Space direction="vertical" style={{ width: "100%" }} size={12}>
                    <Input
                        placeholder={t("searchItemsByName")}
                        value={frequentSearch}
                        onChange={(e) => setFrequentSearch(e.target.value)}
                        allowClear
                    />
                    {frequentProductsLoading ? (
                        <div style={{ textAlign: "center", padding: 24 }}>
                            <Spin />
                        </div>
                    ) : (
                        <div style={{ maxHeight: 400, overflow: "auto" }}>
                            <Table
                                size="small"
                                rowKey={(r) => r._id}
                                pagination={false}
                                dataSource={frequentProducts.filter((p) => p && p._id)}
                                scroll={{ y: 360 }}
                                columns={[
                                    {
                                        title: t("item"),
                                        dataIndex: "title",
                                        render: (_, r) => (
                                            <div style={{ minWidth: 180 }}>
                                                <div style={{ fontWeight: 600 }}>{r.title || tCommon("untitled")}</div>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {formatPKR(r.price)}
                                                </Text>
                                            </div>
                                        ),
                                    },
                                    {
                                        title: t("action"),
                                        width: 150,
                                        align: "right",
                                        render: (_, r) => {
                                            const already = frequentItemIds.has(r._id);
                                            return (
                                                <Space size={6}>
                                                    <Button
                                                        size="small"
                                                        onClick={() => addFrequentItem(r)}
                                                        disabled={already}
                                                    >
                                                        {already ? t("added") : t("addToFrequent")}
                                                    </Button>
                                                </Space>
                                            );
                                        },
                                    },
                                ]}
                            />
                        </div>
                    )}
                </Space>
            </Modal>

            <Modal
                title={selectedTable ? t("createFbrInvoiceForTable", { tableName: selectedTable.name }) : t("createFbrInvoice")}
                open={invoiceModalOpen}
                onCancel={() => setInvoiceModalOpen(false)}
                footer={null}
                width={1100}
                destroyOnHidden
            >
                <Form
                    form={invoiceForm}
                    layout="vertical"
                    onFinish={async (values) => {
                        setInvoiceSubmitting(true);
                        try {
                            await submitFbrInvoice(values);
                            message.success(t("invoiceGeneratedSuccess"));
                            setInvoiceModalOpen(false);
                            await finalizeAndFreeTable();
                            navigate("/invoices", { state: { autoPrint: true } });
                        } catch (e) {
                            message.error(e?.message || t("operationFailed"));
                        } finally {
                            setInvoiceSubmitting(false);
                        }
                    }}
                    initialValues={{
                        invoiceDate: dayjs(),
                        paymentMode: PAYMENT_MODE_CASH,
                    }}
                >
                    <Divider orientation="left">{t("invoiceDetails")}</Divider>
                    <Row gutter={[16, 8]}>
                        <Col span={6}>
                            <Form.Item
                                name="invoiceDate"
                                label={t("invoiceDate")}
                                rules={[{ required: true, message: t("selectInvoiceDate") }]}
                            >
                                <DatePicker className="w-full" format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="customerName" label={t("customerName")}>
                                <Input placeholder={t("customerNamePlaceholder")} />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="customerPhoneNumber" label={t("customerPhoneNumber")}>
                                <Input placeholder={t("customerPhonePlaceholder")} maxLength={11} />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="paymentMode"
                                label={t("paymentMethod")}
                                rules={[{ required: true, message: t("selectPaymentMethod") }]}
                            >
                                <Select
                                    placeholder={t("selectPayment")}
                                    onChange={(value) => {
                                        setPaymentMode(value);
                                        invoiceForm.setFieldsValue({ paymentMode: value });
                                    }}
                                >
                                    <Select.Option value={PAYMENT_MODE_CASH}>{PAYMENT_MODE_CASH}</Select.Option>
                                    <Select.Option value={PAYMENT_MODE_DEBIT_CARD}>{PAYMENT_MODE_DEBIT_CARD}</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider />
                    <Row gutter={[16, 8]}>
                        <Col span={8}>
                            <Card size="small" title={t("totals")}>
                                <Space direction="vertical" style={{ width: "100%" }} size={6}>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <Text>{t("subtotal")}</Text>
                                        <Text>{totals.subTotal.toFixed(2)}</Text>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <Text>{t("govtTaxes")}</Text>
                                        <Text type="danger">+{totals.totalTax.toFixed(2)}</Text>
                                    </div>

                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <Text>{t("perHead")} ({guestCount})</Text>
                                        <Text>{totals.perHeadTotal.toFixed(2)}</Text>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <Text>{t("discount")}</Text>
                                        <Text>-{formatPKR(totals.discountAmount)}</Text>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <Text>{t("furtherTax")}</Text>
                                        <Text>+{totals.furtherTax.toFixed(2)}</Text>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <Text>{t("posFee")}</Text>
                                        <Text>1</Text>
                                    </div>
                                    <Divider style={{ margin: "8px 0" }} />
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <Text strong>{t("total")}</Text>
                                        <Text strong>{totals.finalAmount.toFixed(2)}</Text>
                                    </div>
                                </Space>
                            </Card>
                        </Col>
                        <Col span={16}>
                            <Card size="small" title={t("items")}>
                                <Table
                                    size="small"
                                    rowKey={(r) => r._id}
                                    pagination={false}
                                    dataSource={selectedOrder?.items || []}
                                    columns={[
                                        { title: t("name"), dataIndex: "title" },
                                        { title: t("qty"), dataIndex: "quantity", width: 80, align: "right" },
                                        { title: t("price"), dataIndex: "price", width: 110, align: "right", render: (v) => Number(v || 0).toFixed(2) },
                                        { title: t("tax"), dataIndex: "tax", width: 110, align: "right", render: (v) => Number(v || 0).toFixed(2) },
                                        { title: t("total"), dataIndex: "totalPrice", width: 120, align: "right", render: (v) => Number(v || 0).toFixed(2) },
                                    ]}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                        <Space>
                            <Button onClick={() => setInvoiceModalOpen(false)} disabled={invoiceSubmitting}>
                                {tCommon("cancel")}
                            </Button>
                            <Button type="primary" htmlType="submit" loading={invoiceSubmitting}>
                                {getEnableFBR() 
                                    ? t("sendToFbrAndSaveInvoice") 
                                    : t("saveInvoice")}
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            <Modal
                title={t("kitchenOrderTitle")}
                open={kitchenModalOpen}
                onCancel={() => {
                    setKitchenModalOpen(false);
                    setKitchenSlipData(null);
                }}
                width={400}
                footer={
                    <Space>
                        <Button onClick={() => setKitchenModalOpen(false)}>{t("close")}</Button>
                        <Button type="primary" onClick={onPrintClick}>
                            {t("print")}
                        </Button>
                    </Space>
                }
            >
                {kitchenSlipData && (
                    <div
                        ref={kitchenSlipRef}
                        className="kitchen-slip-thermal"
                        style={thermalSlipStyle}
                    >
                        {/* Small top spacer so first line is not cut off on thermal printers */}
                        <div style={{ height: "3mm" }} />
                        <div style={thermalSlipHeaderStyle}>
                            <div style={thermalSlipTokenStyle}>
                                {t("kitchenTokenLabel")} #{kitchenSlipData.token}
                            </div>
                            <div style={thermalSlipMetaStyle}>
                                {kitchenSlipData.tableName} | {kitchenSlipData.datetime}
                            </div>
                            {kitchenSlipData.waiterName ? (
                                <div style={thermalSlipMetaStyle}>
                                    {t("waiter")}: {kitchenSlipData.waiterName}
                                </div>
                            ) : null}
                            {kitchenSlipData.orderNote ? (
                                <div style={{ ...thermalSlipMetaStyle, marginTop: 4 }}>
                                    {t("orderNote")}: {kitchenSlipData.orderNote}
                                </div>
                            ) : null}
                        </div>
                        <table style={thermalSlipTableStyle}>
                            <thead>
                                <tr>
                                    <th style={thermalSlipThStyle}>{t("item")}</th>
                                    <th style={{ ...thermalSlipThStyle, width: "20%", textAlign: "center" }}>{t("qty")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {kitchenSlipData.items.map((row, i) => (
                                    <tr key={i}>
                                        <td style={thermalSlipTdStyle}>
                                            {row.title || t("product")}
                                            {row.note ? <div style={{ fontSize: 10, marginTop: 1 }}>{row.note}</div> : null}
                                        </td>
                                        <td style={{ ...thermalSlipTdStyle, textAlign: "center" }}>{row.quantity}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>

            {/* Token history: list of past printed receipts */}
            <Modal
                title={t("tokenHistoryTitle")}
                open={tokenHistoryModalOpen}
                onCancel={() => {
                    setTokenHistoryModalOpen(false);
                    setViewingReceipt(null);
                }}
                footer={null}
                width={800}
                destroyOnClose
            >
                {tokenHistoryLoading ? (
                    <div style={{ textAlign: "center", padding: 32 }}>
                        <Spin />
                    </div>
                ) : tokenHistoryList.length === 0 ? (
                    <div style={{ textAlign: "center", padding: 32 }}>
                        <Text type="secondary">{t("noTokenHistory")}</Text>
                    </div>
                ) : (
                    <Table
                        size="small"
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        dataSource={tokenHistoryList}
                        columns={[
                            {
                                title: t("kitchenTokenLabel"),
                                dataIndex: "token",
                                width: 100,
                                render: (token) => <Tag color="blue">#{token}</Tag>,
                            },
                            {
                                title: t("tables"),
                                dataIndex: "tableName",
                                ellipsis: true,
                            },
                            {
                                title: t("dateTime"),
                                dataIndex: "datetime",
                                width: 160,
                            },
                            {
                                title: t("action"),
                                width: 180,
                                minWidth: 180,
                                align: "right",
                                render: (_, record) => (
                                    <Space size="small" wrap={false}>
                                        <Button size="small" type="link" onClick={() => setViewingReceipt(record)}>
                                            {t("view")}
                                        </Button>
                                        <Button size="small" type="link" icon={<PrinterOutlined />} onClick={() => setReprintReceipt(record)}>
                                            {t("reprint")}
                                        </Button>
                                    </Space>
                                ),
                            },
                        ]}
                    />
                )}
            </Modal>

            {/* View single receipt detail – getContainer + zIndex so it appears above Token history modal */}
            <Modal
                title={t("viewReceiptTitle", { token: viewingReceipt?.token ?? "" })}
                open={Boolean(viewingReceipt)}
                onCancel={() => setViewingReceipt(null)}
                footer={
                    <Button type="primary" onClick={() => setViewingReceipt(null)}>
                        {t("close")}
                    </Button>
                }
                width={400}
                getContainer={() => document.body}
                zIndex={1060}
            >
                {viewingReceipt && (
                    <div
                        className="kitchen-slip-thermal"
                        style={thermalSlipStyle}
                    >
                        {/* Spacer to visually match printed slip top margin */}
                        <div style={{ height: "3mm" }} />
                        <div style={thermalSlipHeaderStyle}>
                            <div style={thermalSlipTokenStyle}>
                                {t("kitchenTokenLabel")} #{viewingReceipt.token}
                            </div>
                            <div style={thermalSlipMetaStyle}>
                                {viewingReceipt.tableName} | {viewingReceipt.datetime}
                            </div>
                            {viewingReceipt.waiterName ? (
                                <div style={thermalSlipMetaStyle}>
                                    {t("waiter")}: {viewingReceipt.waiterName}
                                </div>
                            ) : null}
                        </div>
                        <table style={thermalSlipTableStyle}>
                            <thead>
                                <tr>
                                    <th style={thermalSlipThStyle}>{t("item")}</th>
                                    <th style={{ ...thermalSlipThStyle, width: "20%", textAlign: "center" }}>{t("qty")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(viewingReceipt.items || []).map((row, i) => (
                                    <tr key={i}>
                                        <td style={thermalSlipTdStyle}>
                                            {row.title || t("product")}
                                            {row.note ? <div style={{ fontSize: 10, marginTop: 1 }}>{row.note}</div> : null}
                                        </td>
                                        <td style={{ ...thermalSlipTdStyle, textAlign: "center" }}>{row.quantity}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>

            {/* Hidden slip for KOT reprint - ref on inner slip so react-to-print captures content */}
            {reprintReceipt && (
                <div style={{ position: "absolute", left: -9999, top: 0, width: "80mm" }} aria-hidden="true">
                    <div ref={reprintSlipRef} className="kitchen-slip-thermal" style={thermalSlipStyle}>
                        {/* Spacer so reprint content is not clipped at the very top */}
                        <div style={{ height: "3mm" }} />
                        <div style={thermalSlipHeaderStyle}>
                            <div style={thermalSlipTokenStyle}>
                                {t("kitchenTokenLabel")} #{reprintReceipt.token}
                            </div>
                            <div style={thermalSlipMetaStyle}>
                                {reprintReceipt.tableName} | {reprintReceipt.datetime}
                            </div>
                            {reprintReceipt.waiterName ? (
                                <div style={{ ...thermalSlipMetaStyle }}>
                                    {t("waiter")}: {reprintReceipt.waiterName}
                                </div>
                            ) : null}
                            {reprintReceipt.orderNote ? (
                                <div style={{ ...thermalSlipMetaStyle, marginTop: 4 }}>
                                    {t("orderNote")}: {reprintReceipt.orderNote}
                                </div>
                            ) : null}
                        </div>
                        <table style={thermalSlipTableStyle}>
                            <thead>
                                <tr>
                                    <th style={thermalSlipThStyle}>{t("item")}</th>
                                    <th style={{ ...thermalSlipThStyle, width: "20%", textAlign: "center" }}>{t("qty")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(reprintReceipt.items || []).map((row, i) => (
                                    <tr key={i}>
                                        <td style={thermalSlipTdStyle}>
                                            {row.title || t("product")}
                                            {row.note ? <div style={{ fontSize: 10, marginTop: 1 }}>{row.note}</div> : null}
                                        </td>
                                        <td style={{ ...thermalSlipTdStyle, textAlign: "center" }}>{row.quantity}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Merge table modal */}
            <Modal
                title={t("mergeTableTitle")}
                open={mergeModalOpen}
                onCancel={() => { setMergeModalOpen(false); setMergeTargetTableId(null); }}
                onOk={() => mergeTargetTableId && mergeCurrentTableInto(mergeTargetTableId)}
                okText={t("merge")}
                cancelText={tCommon("cancel")}
                okButtonProps={{ disabled: !mergeTargetTableId }}
            >
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Text type="secondary">{t("mergeTableDescription")}</Text>
                    <div>
                        <Text strong>{t("mergeIntoTable")}</Text>
                        <Select
                            placeholder={t("selectTable")}
                            style={{ width: "100%", marginTop: 8 }}
                            value={mergeTargetTableId}
                            onChange={setMergeTargetTableId}
                            options={tables
                                .filter((tb) => tb.id !== selectedTableId)
                                .map((tb) => ({ label: tb.name, value: tb.id }))}
                        />
                    </div>
                </Space>
            </Modal>

            {/* Held orders modal */}
            <Modal
                title={t("heldOrdersTitle")}
                open={heldOrdersModalOpen}
                onCancel={() => setHeldOrdersModalOpen(false)}
                footer={null}
                width={560}
            >
                {heldOrdersLoading ? (
                    <div style={{ textAlign: "center", padding: 32 }}>
                        <Spin />
                    </div>
                ) : heldOrdersList.length === 0 ? (
                    <div style={{ textAlign: "center", padding: 32 }}>
                        <Text type="secondary">{t("noHeldOrders")}</Text>
                    </div>
                ) : (
                    <Table
                        size="small"
                        rowKey="id"
                        pagination={false}
                        dataSource={heldOrdersList}
                        columns={[
                            { title: t("tableName"), dataIndex: "tableName", ellipsis: true },
                            { title: t("items"), dataIndex: "items", render: (items) => Array.isArray(items) ? items.length : 0, width: 70 },
                            {
                                title: t("action"),
                                render: (_, record) => {
                                    const tableName = record.tableName || t("bill");
                                    const targetTable = tables.find((tb) => tb.name === tableName);
                                    return (
                                        <Space size="small" wrap={false}>
                                            {targetTable ? (
                                                <Button
                                                    size="small"
                                                    type="primary"
                                                    onClick={() => resumeHeldOrderToTable(record.id, targetTable.id)}
                                                >
                                                    {t("resumeTableName", { name: tableName })}
                                                </Button>
                                            ) : (
                                                <Text type="secondary">{t("tableNotFound")}</Text>
                                            )}
                                            <Popconfirm
                                                title={t("removeHeldOrderConfirm")}
                                                okText={tCommon("yes")}
                                                cancelText={tCommon("no")}
                                                onConfirm={() => removeHeldOrder(record.id)}
                                            >
                                                <Button size="small" type="text" danger icon={<CloseOutlined />} aria-label={t("remove")} />
                                            </Popconfirm>
                                        </Space>
                                    );
                                },
                            },
                        ]}
                    />
                )}
            </Modal>

            {/* Bill modal: show bill in modal, print from modal (no popup) */}
            <Modal
                title={t("bill")}
                open={billModalOpen}
                onCancel={() => setBillModalOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setBillModalOpen(false)}>{t("close")}</Button>
                        <Button type="primary" icon={<PrinterOutlined />} onClick={onBillPrintClick}>
                            {t("print")}
                        </Button>
                    </Space>
                }
                width={400}
                destroyOnClose
            >
                {billModalOpen && selectedOrder?.items?.length ? (
                    <div
                        ref={billPrintRef}
                        style={{
                            width: "80mm",
                            maxWidth: "80mm",
                            margin: "0 auto",
                            padding: 8,
                            background: "#fff",
                            color: "#000",
                            fontFamily: "Arial, Helvetica, sans-serif",
                            fontSize: 12,
                            boxSizing: "border-box",
                        }}
                    >
                        <h1 style={{ textAlign: "center", margin: "0 0 8px 0", fontSize: 14, borderBottom: "1px dashed #000", paddingBottom: 4 }}>
                            {businessName || t("bill")}
                        </h1>
                        <div style={{ textAlign: "center", fontSize: 11, marginBottom: 8 }}>
                            {selectedTable?.name || t("bill")} — {dayjs().format("DD-MMM-YYYY hh:mm A")}
                        </div>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: "left", padding: "2px 0", borderBottom: "1px solid #000" }}>{t("item")}</th>
                                    <th style={{ textAlign: "center", padding: "2px 0", borderBottom: "1px solid #000" }}>{t("qty")}</th>
                                    <th style={{ textAlign: "right", padding: "2px 0", borderBottom: "1px solid #000" }}>{t("total")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(selectedOrder.items || []).map((it, idx) => (
                                    <tr key={it._id || idx}>
                                        <td style={{ padding: "2px 0", borderBottom: "1px dotted #333" }}>
                                            {it.title || t("product")}{(it.note || "").trim() ? ` (${it.note})` : ""}
                                        </td>
                                        <td style={{ textAlign: "center", padding: "2px 0", borderBottom: "1px dotted #333" }}>
                                            {Number(it.quantity) || 0}
                                        </td>
                                        <td style={{ textAlign: "right", padding: "2px 0", borderBottom: "1px dotted #333" }}>
                                            {Number(it.totalPrice || 0).toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{ marginTop: 8, borderTop: "1px dashed #000", paddingTop: 4, fontSize: 11 }}>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span>{t("subtotal")}</span>
                                <span>{totals.subTotal.toFixed(2)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span>{t("govtTaxes")}</span>
                                <span>+{totals.totalTax.toFixed(2)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span>{t("perHead")}</span>
                                <span>{totals.perHeadTotal.toFixed(2)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span>{t("discount")}</span>
                                <span>-{totals.discountAmount.toFixed(2)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span>{t("furtherTax")}</span>
                                <span>+{totals.furtherTax.toFixed(2)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span>{t("posFee")}</span>
                                <span>{totals.posFee.toFixed(2)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, marginTop: 4 }}>
                                <span>{t("total")}</span>
                                <span>{totals.finalAmount.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                ) : null}
            </Modal>

            {/* Bulk add tables modal */}
            <Modal
                title={t("bulkAddTablesTitle")}
                open={bulkTablesModalOpen}
                onCancel={() => !bulkTablesSubmitting && setBulkTablesModalOpen(false)}
                footer={null}
                closable={!bulkTablesSubmitting}
                width={400}
                destroyOnClose
            >
                <Space direction="vertical" style={{ width: "100%" }} size={16}>
                    <Form.Item label={t("numberOfTablesToAdd")} style={{ marginBottom: 0 }}>
                        <InputNumber
                            min={2}
                            max={100}
                            value={bulkTablesCount}
                            onChange={(v) => setBulkTablesCount(v != null ? Math.min(100, Math.max(2, Math.floor(Number(v)) || 2)) : 10)}
                            style={{ width: "100%" }}
                            disabled={bulkTablesSubmitting}
                        />
                    </Form.Item>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <Button onClick={() => setBulkTablesModalOpen(false)} disabled={bulkTablesSubmitting}>
                            {tCommon("cancel")}
                        </Button>
                        <Button
                            type="primary"
                            loading={bulkTablesSubmitting}
                            onClick={async () => {
                                setBulkTablesSubmitting(true);
                                try {
                                    await createTablesBulk(bulkTablesCount);
                                } catch (e) {
                                    message.error(e?.message || t("operationFailed"));
                                } finally {
                                    setBulkTablesSubmitting(false);
                                }
                            }}
                        >
                            {t("addTablesConfirm", { count: bulkTablesCount })}
                        </Button>
                    </div>
                </Space>
            </Modal>

            {/* Bulk delete tables modal */}
            <Modal
                title={t("bulkDeleteTablesTitle")}
                open={bulkDeleteModalOpen}
                onCancel={() => !bulkDeleteSubmitting && (setBulkDeleteModalOpen(false), setBulkDeleteSelectedKeys([]))}
                footer={null}
                closable={!bulkDeleteSubmitting}
                width={480}
                destroyOnClose
            >
                {tables.length === 0 ? (
                    <Text type="secondary">{t("noTablesToDelete")}</Text>
                ) : (
                    <Space direction="vertical" style={{ width: "100%" }} size={16}>
                        <Text type="secondary">{t("bulkDeleteTablesDescription")}</Text>
                        <Table
                            size="small"
                            rowKey="id"
                            pagination={false}
                            scroll={{ y: 320 }}
                            dataSource={tables}
                            rowSelection={{
                                selectedRowKeys: bulkDeleteSelectedKeys,
                                onChange: (keys) => setBulkDeleteSelectedKeys(keys),
                            }}
                            columns={[
                                { title: t("tables"), dataIndex: "name", ellipsis: true },
                                {
                                    title: t("status"),
                                    key: "status",
                                    width: 100,
                                    render: (_, r) => (
                                        <Tag color={busyTableIds.has(r.id) ? "orange" : "green"}>
                                            {busyTableIds.has(r.id) ? t("busy") : t("free")}
                                        </Tag>
                                    ),
                                },
                            ]}
                        />
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <Button
                                onClick={() => (setBulkDeleteModalOpen(false), setBulkDeleteSelectedKeys([]))}
                                disabled={bulkDeleteSubmitting}
                            >
                                {tCommon("cancel")}
                            </Button>
                            <Button
                                danger
                                type="primary"
                                loading={bulkDeleteSubmitting}
                                disabled={bulkDeleteSelectedKeys.length === 0}
                                onClick={async () => {
                                    if (bulkDeleteSelectedKeys.length === 0) return;
                                    setBulkDeleteSubmitting(true);
                                    try {
                                        await deleteTablesBulk(bulkDeleteSelectedKeys);
                                    } finally {
                                        setBulkDeleteSubmitting(false);
                                    }
                                }}
                            >
                                {t("deleteTablesConfirm", { count: bulkDeleteSelectedKeys.length })}
                            </Button>
                        </div>
                    </Space>
                )}
            </Modal>
        </>
    );
};

export default DiningPage;